import { isMocked } from "@constants/common";
import { useAppStore } from "@hooks/useAppStore";
import {
	calculateAccuracy,
	convertToMinutes,
	formatSecondsToDurationLabel,
	formatSecondsToUTCtime,
} from "@memorang/helpers/common";
import {
	formatDate,
	formatDateTime,
	normalizeDate,
} from "@memorang/helpers/date";
import {
	calculatePointsPercentage,
	examCoversSkill,
} from "@memorang/helpers/language-learning";
import { isSkillTag } from "@memorang/helpers/tag";
import type {
	Distribution,
	ExamDetails,
	ExamStatus,
	MixedItemTag,
	PracticeTestExam,
	StateDetails,
} from "@memorang/types";
import type { TaskChip } from "@memorang/ui/components/HomeApplet/types";
import {
	getSkillIcon,
	resolveTagIcon,
	skillIconMap,
} from "@memorang/ui/constants";
import { type IconType, isIconType } from "@memorang/ui/icon";
import i18n from "./lib/i18n";

export const getIsOlxExam = (id: string) => id.includes("OLX");

export const isUnfinished = (exam: PracticeTestExam) =>
	exam.status !== "COMPLETE";

export const isInProgress = (exam: PracticeTestExam) =>
	exam.status === "ACTIVE";

export const isDiagnostic = (exam: PracticeTestExam) =>
	exam.subType === "DIAGNOSTIC";

export const getSkillNamesForTest = (
	test: PracticeTestExam,
	skills: MixedItemTag[],
): string[] => {
	const fromSkills = skills
		.filter((skill) => examCoversSkill(test, skill))
		.map((skill) => skill.title);
	if (fromSkills.length > 0) {
		return fromSkills;
	}
	return (test.topics ?? []).filter((topic) => topic.length > 0);
};

const ITEMS_CHIP_ICON = "card-multiple-outline" as const;
const DURATION_CHIP_ICON = "clock-outline" as const;
const TIME_LEFT_CHIP_ICON = "timer-sand" as const;
const DATE_CHIP_ICON = "calendar-check-outline" as const;

const SECONDS_PER_HOUR = 3600;

const DIAGNOSTIC_FALLBACK_SKILL_ICON = "school-outline" as const;

/**
 * Glyph shown inside the completion ring for anything that isn't a diagnostic.
 * Completed tests are absent because they keep their score donut instead.
 */
const STATUS_RING_ICONS: Partial<Record<ExamStatus, IconType>> = {
	NOT_STARTED: "clipboard-check-outline",
	ACTIVE: "progress-clock",
};

/** Chips are tight on horizontal space, so their dates drop the year. */
const CHIP_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
	year: undefined,
	month: "short",
	day: "numeric",
};

/**
 * Completed-date chips show just the date: "Aug 7" this year, otherwise
 * "Aug 7, 2025".
 */
const formatCompletedChipDate = (dateCompleted: string): string => {
	const date = normalizeDate(dateCompleted);
	if (!date) {
		return "";
	}
	const isCurrentYear = date.getFullYear() === new Date().getFullYear();
	return formatDate(dateCompleted, undefined, {
		month: "short",
		day: "numeric",
		year: isCurrentYear ? undefined : "numeric",
	});
};

const getNumAttempted = (attempted?: Distribution) =>
	attempted ? attempted.high + attempted.low + attempted.medium : 0;

const buildDateChip = (labelKey: string, date: string): TaskChip[] =>
	date ? [{ icon: DATE_CHIP_ICON, label: i18n.t(labelKey, { date }) }] : [];

/**
 * Minute-scale durations read better spelled out, but a full-length test would
 * become "120 min", so anything an hour or longer falls back to "2h 30m".
 */
const formatDurationLabel = (totalTime: number) =>
	totalTime >= SECONDS_PER_HOUR
		? formatSecondsToDurationLabel(totalTime)
		: i18n.t("sessionConfig.quickStudy.duration", {
				minutes: convertToMinutes(totalTime),
			});

export const getDescAndCTA = ({
	status,
	numItems,
	totalTime,
	dateCompleted,
	timeElapsed,
	attempted,
	endStartDate,
	createdAt,
}: PracticeTestExam) => {
	if (status === "COMPLETE") {
		return {
			description: i18n.t("dashboard.practiceTests.completedDescription", {
				count: numItems,
				date: formatDate(dateCompleted),
			}),
			cta: i18n.t("dashboard.practiceTests.viewResultsCta"),
		};
	}
	if (status === "ACTIVE") {
		const timeRemaining = totalTime - timeElapsed;
		const numAttempts = getNumAttempted(attempted);
		const description =
			endStartDate && createdAt
				? i18n.t("dashboard.practiceTests.activeDescriptionWithDates", {
						attempted: numAttempts,
						count: numItems,
						timeRemaining: formatSecondsToUTCtime(timeRemaining),
						startedOn: formatDate(createdAt),
						endsOn: formatDateTime(endStartDate),
					})
				: i18n.t("dashboard.practiceTests.activeDescription", {
						attempted: numAttempts,
						count: numItems,
						timeRemaining: formatSecondsToUTCtime(timeRemaining),
					});
		return {
			description,
			cta: i18n.t("dashboard.recommendedTasks.resumeAction"),
		};
	}
	if (status === "NOT_STARTED") {
		return {
			description: endStartDate
				? i18n.t("dashboard.practiceTests.notStartedDescriptionWithEnd", {
						count: numItems,
						duration: formatSecondsToUTCtime(totalTime),
						endsOn: formatDateTime(endStartDate),
					})
				: i18n.t("dashboard.practiceTests.notStartedDescription", {
						count: numItems,
						duration: formatSecondsToUTCtime(totalTime),
					}),
			cta: i18n.t("dashboard.practiceTests.startCta"),
		};
	}
	return {
		description: i18n.t("dashboard.practiceTests.notStartedDescription", {
			count: numItems,
			duration: formatSecondsToUTCtime(totalTime),
		}),
		cta: i18n.t("dashboard.practiceTests.startCta"),
	};
};
export const getExamChips = ({
	status,
	numItems,
	totalTime,
	dateCompleted,
	timeElapsed,
	attempted,
	endStartDate,
	createdAt,
}: PracticeTestExam): TaskChip[] => {
	if (status === "COMPLETE") {
		return [
			{
				icon: ITEMS_CHIP_ICON,
				label: i18n.t("dashboard.practiceTests.chips.items", {
					count: numItems,
				}),
			},
			...(dateCompleted
				? [
						{
							icon: DATE_CHIP_ICON,
							label: formatCompletedChipDate(dateCompleted),
						},
					]
				: []),
		];
	}

	if (status === "ACTIVE") {
		return [
			{
				icon: ITEMS_CHIP_ICON,
				label: i18n.t("dashboard.practiceTests.chips.progress", {
					attempted: getNumAttempted(attempted),
					count: numItems,
				}),
			},
			{
				icon: TIME_LEFT_CHIP_ICON,
				label: i18n.t("dashboard.practiceTests.chips.timeLeft", {
					time: formatSecondsToUTCtime(totalTime - timeElapsed),
				}),
			},
			...(endStartDate && createdAt
				? [
						...buildDateChip(
							"dashboard.practiceTests.chips.startedOn",
							formatDate(createdAt, undefined, CHIP_DATE_OPTIONS),
						),
						...buildDateChip(
							"dashboard.practiceTests.chips.endsOn",
							formatDateTime(endStartDate, undefined, CHIP_DATE_OPTIONS),
						),
					]
				: []),
		];
	}

	return [
		{
			icon: ITEMS_CHIP_ICON,
			label: i18n.t("dashboard.practiceTests.chips.items", {
				count: numItems,
			}),
		},
		{
			icon: DURATION_CHIP_ICON,
			label: formatDurationLabel(totalTime),
		},
		...buildDateChip(
			"dashboard.practiceTests.chips.endsOn",
			endStartDate
				? formatDateTime(endStartDate, undefined, CHIP_DATE_OPTIONS)
				: "",
		),
	];
};

export const calculateScore = (distribution: Distribution) => {
	const { high, low, medium, none } = distribution;

	return calculateAccuracy({
		numCorrect: high,
		numTotal: high + low + medium + none,
	});
};

/**
 * Icon for the skill an exam covers. `tagDetails` is the structural signal but
 * is empty on exams that were never started, so this falls back to `topics` and
 * finally to matching a known skill inside the title (e.g. "Diagnostic: Listening").
 */
export const getExamSkillIcon = ({
	tagDetails,
	topics,
	title,
}: Pick<PracticeTestExam, "tagDetails" | "topics" | "title">): IconType => {
	const skillTag = tagDetails?.find(isSkillTag);
	if (skillTag) {
		const tagIcon = resolveTagIcon(skillTag, skillIconMap);
		// The ring draws through AppIcon, so a custom level glyph can't be used.
		if (tagIcon && isIconType(tagIcon)) {
			return tagIcon;
		}
	}

	const topic = topics?.[0];
	if (topic) {
		return getSkillIcon(topic);
	}

	const titledSkill = Object.keys(skillIconMap).find((skill) =>
		title.toLocaleLowerCase().includes(skill.toLocaleLowerCase()),
	);

	return titledSkill
		? skillIconMap[titledSkill]
		: DIAGNOSTIC_FALLBACK_SKILL_ICON;
};

/**
 * Ring tracking how much of an assessment is answered. Diagnostics use it for
 * every status, since they report completion rather than a score; practice and
 * sectional tests use it until they finish and switch to their score donut.
 */
const getCompletionRing = (exam: PracticeTestExam, status: ExamStatus) => {
	const icon =
		exam.subType === "DIAGNOSTIC"
			? getExamSkillIcon(exam)
			: STATUS_RING_ICONS[status];

	if (!icon) {
		return undefined;
	}

	const getProgress = () => {
		if (status !== "ACTIVE") {
			return status === "NOT_STARTED" ? 0 : 100;
		}
		return exam.numItems
			? Math.round((getNumAttempted(exam.attempted) / exam.numItems) * 100)
			: 0;
	};

	return { progress: getProgress(), icon };
};

/**
 * Calculates exam score based on number of points earned out of total points,
 * when present. Otherwise, falls back to distribution accuracy.
 */
const getExamScorePercent = (exam: PracticeTestExam): number => {
	const { status, subType, numPointsEarned, numPointsTotal, distribution } =
		exam;
	const hasCoherentPoints =
		status === "COMPLETE" &&
		subType !== "DIAGNOSTIC" &&
		numPointsEarned != null &&
		numPointsTotal != null &&
		numPointsEarned > 0 &&
		numPointsTotal > 0 &&
		numPointsEarned <= numPointsTotal;
	if (hasCoherentPoints) {
		return calculatePointsPercentage(numPointsEarned, numPointsTotal);
	}
	return distribution ? calculateScore(distribution) : 0;
};

export const getMappedExams = (exams: PracticeTestExam[]) => {
	const mappedExams = exams.map((exam) => {
		const {
			title,
			status,
			id,
			numericId,
			collectionId,
			numPointsEarned,
			numPointsTotal,
		} = exam;
		const { description, cta } = getDescAndCTA(exam);
		const score = getExamScorePercent(exam);
		const resolvedStatus = status || ("NOT_STARTED" as const);
		return {
			title,
			description,
			chips: getExamChips(exam),
			completionRing: getCompletionRing(exam, resolvedStatus),
			collectionId,
			status: resolvedStatus,
			numericId: numericId || 0,
			cta,
			id,
			score,
			numPointsEarned,
			numPointsTotal,
			hasExamAccess: isMocked || exam.hasAccess,
			type: exam.type!,
			subType: exam.subType!,
			beginStartDate: exam.beginStartDate,
			endStartDate: exam.endStartDate,
		};
	});

	const legacyReports = exams.filter((item) => item.legacyReport != null);
	const migratedReports = exams.filter(
		(item) => item.migratedSessionReport != null,
	);

	if (legacyReports.length > 0) {
		const legacyReport = legacyReports.map((item) => {
			const { title, legacyReport } = item;
			const completedExam = {
				...item,
				status: "COMPLETE" as const,
				...legacyReport!,
			};
			const { description, cta } = getDescAndCTA(completedExam);
			const { numericId, id, score } = legacyReport!;

			return {
				title,
				description,
				chips: getExamChips(completedExam),
				completionRing: getCompletionRing(completedExam, "LEGACYREPORT"),
				status: "LEGACYREPORT" as const,
				numericId: numericId || 0,
				cta,
				id,
				collectionId: item.collectionId,
				score: Math.round(score),
				numPointsEarned: item.numPointsEarned,
				numPointsTotal: item.numPointsTotal,
				hasExamAccess: true,
				subType: item.subType!,
				type: item.type!,
				beginStartDate: item.beginStartDate,
				endStartDate: item.endStartDate,
				legacyReport: item.legacyReport,
				migratedSessionReport: item.migratedSessionReport,
			};
		});
		mappedExams.push(...legacyReport);
	}

	if (migratedReports.length > 0) {
		const migratedReport = migratedReports.map((item) => {
			const { title, migratedSessionReport } = item;
			const completedExam = {
				...item,
				status: "COMPLETE" as const,
				...migratedSessionReport!,
			};
			const { description, cta } = getDescAndCTA(completedExam);
			const { numericId, id, score } = migratedSessionReport!;
			return {
				title,
				description,
				chips: getExamChips(completedExam),
				completionRing: getCompletionRing(completedExam, "MIGRATEDREPORT"),
				status: "MIGRATEDREPORT" as const,
				numericId: numericId || 0,
				cta,
				id,
				collectionId: item.collectionId,
				score: Math.round(score),
				numPointsEarned: item.numPointsEarned,
				numPointsTotal: item.numPointsTotal,
				hasExamAccess: true,
				type: item.type!,
				subType: item.subType!,
				beginStartDate: item.beginStartDate,
				endStartDate: item.endStartDate,
				legacyReport: item.legacyReport,
				migratedSessionReport: item.migratedSessionReport,
			};
		});
		mappedExams.push(...migratedReport);
	}

	return mappedExams;
};

export const getShowStudyPackViews = (
	studyPackProductCollectionId?: number,
) => {
	const isStudyPackEnabled = useAppStore.getState().app?.isStudyPackEnabled;

	return isStudyPackEnabled && Boolean(studyPackProductCollectionId);
};

export const isOlxExamWithinDateRange = (
	beginStartDate?: string,
	endStartDate?: string,
): boolean => {
	if (!(beginStartDate && endStartDate)) {
		return true;
	}

	const beginTime = new Date(beginStartDate).getTime();
	const endTime = new Date(endStartDate).getTime();
	const currentTime = Date.now();

	return currentTime >= beginTime && currentTime <= endTime;
};

export const getOLXExamEligibilityConfig = (
	startDate: string,
	endDate: string,
) => {
	const currentTime = Date.now();
	const beginTime = new Date(startDate).getTime();
	const endTime = new Date(endDate).getTime();

	if (currentTime < beginTime) {
		return {
			title: "Exam Not Yet Available",
			body: `You have unlocked the OLX exam, but you cannot start it yet. The exam will be available on ${formatDateTime(
				startDate,
			)}. Please check back then.`,
		};
	}
	if (currentTime > endTime) {
		return {
			title: "Exam Window Closed",
			body: "Unfortunately, the window to take your OLX exam has passed. Please contact your professor or school administrator for further assistance.",
		};
	}
	return {
		title: "Start Your OLX Exam",
		body: `You have unlocked the OLX exam. You can start the exam now and continue until ${formatDateTime(
			endDate,
		)} from the practice tests tab.`,
		cta: "Go to Practice tests",
	};
};

const isOlxProduct = (name?: string) => name?.includes("OLX");

export const groupExamsByCategory = (
	examList: ExamDetails[],
): {
	title: string;
	icon: string;
	data: ExamDetails[];
}[] => {
	const groupedData = examList.reduce<Record<string, ExamDetails[]>>(
		(acc, cur) => {
			const category = cur.exam.category[0].name;

			if (!acc[category]) {
				acc[category] = [];
			}

			const filteredCur = {
				...cur,
				exam: {
					...cur.exam,
					bundles: cur.exam.bundles
						.slice()
						.sort((a, b) => a.bundle.numericId - b.bundle.numericId)
						.filter((item) => {
							return !isOlxProduct(item.bundle.name);
						}),
				},
			};

			acc[category].push(filteredCur);

			return acc;
		},
		{},
	);

	const output = Object.keys(groupedData).map((category) => ({
		title: category,
		icon: groupedData[category][0].exam.category[0].icon,
		data: groupedData[category],
	}));

	return output;
};

/**
 * Extracts unique states from exams belonging to a specific category
 */
export const getStatesForCategory = (
	exams: ExamDetails[],
	categoryId: string,
) => {
	const categoryExams = exams.filter((exam) =>
		exam.exam.category.some((c) => c.id === categoryId),
	);

	const statesMap = new Map<string, StateDetails[]>();

	for (const exam of categoryExams) {
		if (exam.exam.state) {
			if (!statesMap.has(exam.exam.state.code)) {
				statesMap.set(exam.exam.state.code, []);
			}
			statesMap.get(exam.exam.state.code)?.push(exam.exam.state);
		}
	}

	return Array.from(statesMap.values());
};
export const getRelatedExams = (
	exams: ExamDetails[],
	selectedExams?: ExamDetails[],
): ExamDetails[] => {
	if (!selectedExams?.length) {
		return [];
	}

	const relatedExamIds =
		selectedExams?.map((exam) => exam.exam.relatedExams) || [];
	const relatedExams = exams.filter((exam) =>
		relatedExamIds.flat().includes(exam.exam.id),
	);

	return [...selectedExams, ...relatedExams];
};

export const getFilteredExams = (
	exams: ExamDetails[],
	categoryId?: string,
	level?: "state" | "national" | "state-national",
	stateCode?: string,
): ExamDetails[] => {
	if (!categoryId) {
		return exams;
	}
	let filtered = exams.filter((exam) =>
		exam.exam.category.some((c) => c.id === categoryId),
	);

	switch (level) {
		case "national":
			filtered = filtered.filter((exam) => !exam.exam.state);
			break;
		case "state":
		case "state-national":
			{
				const selectedExams = filtered.filter(
					(exam) => exam.exam.state?.code === stateCode,
				);
				filtered = getRelatedExams(filtered, selectedExams);
			}
			break;
		default:
			return filtered;
	}

	return filtered;
};
